import React, { useEffect, useState } from "react";
import {
  Stack,
  Grid,
  Group,
  Title,
  Text,
  Paper,
  TextInput,
  Button,
  ActionIcon,
  Box,
  Image as MantineImage,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  Dropzone,
  IMAGE_MIME_TYPE,
  type FileWithPath,
} from "@mantine/dropzone";
import { notifications } from "@mantine/notifications";
import { MAX_UPLOAD_SIZE, rejectionMessage } from "../../../utils/uploads";
import {
  IconArrowLeft,
  IconUpload,
  IconPhoto,
  IconX,
} from "@tabler/icons-react";
import { router, usePage } from "@inertiajs/react";
import type { AdminVehicleForm } from "./types";

type VehicleFormProps = {
  vehicle: AdminVehicleForm;
};

type VersionValues = {
  id: number;
  modele: string;
  sra_commercial: string;
  test_drive_url: string;
  photo: FileWithPath | null;
  remove_photo: boolean;
};

type FormValues = {
  name: string;
  test_drive_url: string;
  photo: FileWithPath | null;
  remove_photo: boolean;
  vehicle_versions: VersionValues[];
};

const PhotoField = ({
  photo,
  photoUrl,
  removePhoto,
  error,
  onDrop,
  onRemove,
}: {
  photo: FileWithPath | null;
  photoUrl: string | null;
  removePhoto: boolean;
  error?: React.ReactNode;
  onDrop: (file: FileWithPath) => void;
  onRemove: () => void;
}) => {
  // Certaines URLs externes bloquent l'affichage embarqué depuis un autre
  // domaine : on évite l'icône cassée native du navigateur.
  const [loadError, setLoadError] = useState(false);

  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!photo) {
      setObjectUrl(null);
      return;
    }
    const url = URL.createObjectURL(photo);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);

  const preview = removePhoto ? null : (objectUrl ?? photoUrl);

  useEffect(() => {
    setLoadError(false);
  }, [preview]);

  return (
    <Stack gap="xs">
      {preview && loadError && (
        <Box
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: 140,
            borderRadius: "var(--mantine-radius-md)",
            background: "var(--mantine-color-gray-1)",
          }}
        >
          <Text size="xs" c="dimmed" ta="center" px="md">
            Aperçu indisponible (l'hébergeur externe bloque l'affichage depuis
            ce site). Importez un fichier pour la remplacer.
          </Text>
        </Box>
      )}
      {preview && !loadError && (
        <Box style={{ position: "relative" }}>
          <MantineImage
            src={preview}
            alt="Photo du véhicule"
            radius="md"
            h={140}
            fit="contain"
            onError={() => setLoadError(true)}
          />
          <ActionIcon
            type="button"
            size="sm"
            color="dark"
            variant="filled"
            style={{ position: "absolute", top: 6, right: 6 }}
            onClick={onRemove}
          >
            <IconX size={12} />
          </ActionIcon>
        </Box>
      )}
      <Dropzone
        accept={IMAGE_MIME_TYPE}
        maxSize={MAX_UPLOAD_SIZE}
        multiple={false}
        onDrop={(files) => onDrop(files[0])}
        // Sans onReject, un fichier hors limites etait ecarte en silence :
        // rien ne bougeait dans l'interface et la cause restait invisible.
        onReject={(rejections) =>
          notifications.show({
            color: "red",
            title: "Photo du véhicule",
            message: rejectionMessage(rejections),
          })
        }
      >
        <Group
          justify="center"
          gap="md"
          mih={80}
          style={{ pointerEvents: "none" }}
        >
          <Dropzone.Accept>
            <IconUpload size={32} />
          </Dropzone.Accept>
          <Dropzone.Idle>
            <IconPhoto size={32} />
          </Dropzone.Idle>
          <Text size="sm" c="dimmed">
            Glissez une image ou cliquez pour sélectionner
          </Text>
        </Group>
      </Dropzone>
      {error && (
        <Text size="xs" c="red">
          {error}
        </Text>
      )}
    </Stack>
  );
};

const AdminVehiclesForm: React.FC<VehicleFormProps> = ({ vehicle }) => {
  const { props } = usePage<{ errors?: Record<string, string> }>();

  const form = useForm<FormValues>({
    initialValues: {
      name: vehicle.name,
      test_drive_url: vehicle.test_drive_url ?? "",
      photo: null,
      remove_photo: false,
      vehicle_versions: vehicle.vehicle_versions.map((version) => ({
        id: version.id,
        modele: version.modele ?? "",
        sra_commercial: version.sra_commercial ?? "",
        test_drive_url: version.test_drive_url ?? "",
        photo: null,
        remove_photo: false,
      })),
    },
    validate: {
      name: (v) => (v.trim().length === 0 ? "Le nom est requis." : null),
    },
  });

  useEffect(() => {
    if (props.errors && Object.keys(props.errors).length > 0) {
      form.setErrors(props.errors);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.errors]);

  const buildFormData = (values: FormValues) => {
    const fd = new FormData();
    fd.append("vehicle[name]", values.name);
    fd.append("vehicle[test_drive_url]", values.test_drive_url);
    if (values.remove_photo) {
      fd.append("vehicle[remove_photo]", "1");
    } else if (values.photo) {
      fd.append("vehicle[photo]", values.photo);
    }
    values.vehicle_versions.forEach((version, index) => {
      fd.append(`vehicle_versions[${index}][id]`, String(version.id));
      fd.append(`vehicle_versions[${index}][modele]`, version.modele);
      fd.append(
        `vehicle_versions[${index}][sra_commercial]`,
        version.sra_commercial,
      );
      fd.append(
        `vehicle_versions[${index}][test_drive_url]`,
        version.test_drive_url,
      );
      if (version.remove_photo) {
        fd.append(`vehicle_versions[${index}][remove_photo]`, "1");
      } else if (version.photo) {
        fd.append(`vehicle_versions[${index}][photo]`, version.photo);
      }
    });
    return fd;
  };

  const submit = (values: FormValues) => {
    const onSuccess = () =>
      notifications.show({
        color: "green",
        title: "Véhicule",
        message: "Véhicule enregistré.",
      });
    const onError = () =>
      notifications.show({
        color: "red",
        title: "Véhicule",
        message:
          "Le véhicule n'a pas pu être enregistré. Vérifiez les champs en rouge.",
      });

    const data = buildFormData(values);
    data.append("_method", "patch");
    router.post(`/admin/vehicles/${vehicle.id}`, data, {
      onSuccess,
      onError,
    });
  };

  const handleSubmit = (values: FormValues) => submit(values);

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="xl">
        <Group gap={6} align="center">
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            onClick={() => router.visit("/admin/vehicles")}
          >
            <IconArrowLeft size={15} />
          </ActionIcon>
          <Text size="sm" c="dimmed">
            Véhicules
          </Text>
          <Text size="sm" c="dimmed">
            /
          </Text>
          <Title order={2}>Éditer le véhicule</Title>
        </Group>

        <Grid gap="xl">
          <Grid.Col span={{ base: 12, md: 8 }}>
            <Stack gap="xl">
              <Paper p="lg" radius="md" withBorder>
                <Stack gap="md">
                  <Text size="sm" fw={700}>
                    Informations générales
                  </Text>
                  <TextInput
                    label="Nom du modèle"
                    withAsterisk
                    {...form.getInputProps("name")}
                  />

                  <TextInput
                    label="Lien de demande d'essai"
                    placeholder="https://exemple.fr/essai"
                    description="Affiché sur toute la gamme. Une finition qui a son
                      propre lien le remplace. Laissez vide pour n'afficher aucun
                      bouton."
                    {...form.getInputProps("test_drive_url")}
                  />

                  <Stack gap="xs">
                    <Text size="sm" fw={600}>
                      Photo
                    </Text>
                    <Text size="xs" c="dimmed">
                      Image par défaut de la gamme : utilisée dans les listes et
                      à l&apos;ouverture de la fiche. Sans elle, on retombe sur
                      la photo de la première version.
                    </Text>
                    <PhotoField
                      photo={form.values.photo}
                      photoUrl={vehicle.vehicle_photo_url}
                      removePhoto={form.values.remove_photo}
                      error={form.errors.photo}
                      onDrop={(file) => {
                        form.setFieldValue("photo", file);
                        form.setFieldValue("remove_photo", false);
                      }}
                      onRemove={() => {
                        form.setFieldValue("photo", null);
                        form.setFieldValue("remove_photo", true);
                      }}
                    />
                  </Stack>
                </Stack>
              </Paper>

              {form.values.vehicle_versions.map((version, index) => (
                <Paper key={version.id} p="lg" radius="md" withBorder>
                  <Stack gap="md">
                    <Text size="sm" fw={700}>
                      Finition — {version.sra_commercial || `#${version.id}`}
                    </Text>
                    <TextInput
                      label="Nom du modèle"
                      {...form.getInputProps(
                        `vehicle_versions.${index}.modele`,
                      )}
                    />
                    <TextInput
                      label="Nom commercial"
                      {...form.getInputProps(
                        `vehicle_versions.${index}.sra_commercial`,
                      )}
                    />

                    <TextInput
                      label="Lien de demande d'essai"
                      placeholder="https://exemple.fr/essai"
                      description="Prioritaire sur le lien de la gamme quand cette
                        finition est sélectionnée. Vide = lien de la gamme."
                      {...form.getInputProps(
                        `vehicle_versions.${index}.test_drive_url`,
                      )}
                    />

                    <Stack gap="xs">
                      <Text size="sm" fw={600}>
                        Photo
                      </Text>
                      <PhotoField
                        photo={version.photo}
                        photoUrl={
                          vehicle.vehicle_versions[index]?.photo_url ?? null
                        }
                        removePhoto={version.remove_photo}
                        error={form.errors[`vehicle_versions.${index}.photo`]}
                        onDrop={(file) => {
                          form.setFieldValue(
                            `vehicle_versions.${index}.photo`,
                            file,
                          );
                          form.setFieldValue(
                            `vehicle_versions.${index}.remove_photo`,
                            false,
                          );
                        }}
                        onRemove={() => {
                          form.setFieldValue(
                            `vehicle_versions.${index}.photo`,
                            null,
                          );
                          form.setFieldValue(
                            `vehicle_versions.${index}.remove_photo`,
                            true,
                          );
                        }}
                      />
                    </Stack>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 4 }}>
            <Paper p="lg" radius="md" withBorder>
              <Stack gap="md">
                <Text size="sm" fw={700}>
                  Marque
                </Text>
                <Text size="sm">{vehicle.brand_name}</Text>
              </Stack>
            </Paper>
          </Grid.Col>
        </Grid>

        <Group justify="flex-start">
          <Button type="submit" color="brand">
            Enregistrer le véhicule
          </Button>
        </Group>
      </Stack>
    </form>
  );
};

export default AdminVehiclesForm;
